// ResultatsClient.jsx - Version avec Tailwind CSS natif
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import {
  Award, Users, GraduationCap, TrendingUp, Loader2, AlertCircle,
  Download, MessageSquare, ChevronLeft, ChevronRight, ChevronDown,
  FileQuestion, X, Star, BarChart3
} from 'lucide-react';

// ============================================================
// 1. COMPOSANTS UTILITAIRES
// ============================================================
const StatCard = ({ title, value, icon: IconComponent, color }) => (
  <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">{title}</p>
        <p className="text-2xl font-black text-slate-800 mt-1">{value}</p>
      </div>
      <div className={`p-2.5 rounded-xl ${color}`}>
        {IconComponent && <IconComponent size={18} className="text-white" />}
      </div>
    </div>
  </div>
);

const STATUT_BADGES = {
  REUSSI: 'bg-green-100 text-green-700',
  ECHEC: 'bg-red-100 text-red-700',
  EN_ATTENTE_CORRECTION: 'bg-amber-100 text-amber-700',
};

const StatutBadge = ({ statut }) => (
  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${STATUT_BADGES[statut] || 'bg-slate-100 text-slate-600'}`}>
    {(statut || 'N/A').replace(/_/g, ' ')}
  </span>
);

const STATUTS_FILTRE = [
  { value: '', label: 'Tous les statuts' },
  { value: 'REUSSI', label: 'Réussi' },
  { value: 'ECHEC', label: 'Échec' },
  { value: 'EN_ATTENTE_CORRECTION', label: 'En attente de correction' },
];

// ============================================================
// 2. COMPOSANT PRINCIPAL
// ============================================================
export default function ResultatsClient() {
  const { token, isAuthenticated } = useAuth();

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  }), [token]);

  // --- Type de résultat : 'classic' ou 'rich' ---
  const [resultType, setResultType] = useState('classic');

  const [rapport, setRapport] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [evaluationId, setEvaluationId] = useState('');
  const [statut, setStatut] = useState('');

  const [resultats, setResultats] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, size: 15, totalPages: 0, totalElements: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [appreciationTarget, setAppreciationTarget] = useState(null);
  const [appreciationValue, setAppreciationValue] = useState(5);
  const [commentaireValue, setCommentaireValue] = useState('');
  const [savingAppreciation, setSavingAppreciation] = useState(false);

  const [downloadingId, setDownloadingId] = useState(null);

  // ============================================================
  // 3. CHARGEMENT INITIAL : STATS + LISTE DES ÉVALUATIONS (selon type)
  // ============================================================
  useEffect(() => {
    if (!token || !isAuthenticated) return;
    // Statistiques générales (inchangées)
    fetch(`${APP_CONFIG.BASE_URL}/api/v1/client/rapport/statistiques`, { headers: authHeaders })
      .then((res) => (res.ok ? res.json() : null))
      .then(setRapport)
      .catch((err) => console.warn('Statistiques indisponibles', err));

    // Charger les évaluations selon le type
    const endpoint = resultType === 'rich'
      ? '/api/v1/client/rich/evaluations'
      : '/api/v1/client/evaluations';
    fetch(`${APP_CONFIG.BASE_URL}${endpoint}`, { headers: authHeaders })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const list = data.content || data.data || data || [];
        setEvaluations(list);
        // Réinitialiser la sélection
        setEvaluationId('');
        setResultats([]);
      })
      .catch((err) => console.warn('Évaluations indisponibles', err));
  }, [token, isAuthenticated, authHeaders, resultType]);

  // ============================================================
  // 4. CHARGEMENT DES RÉSULTATS (FILTRÉS + PAGINÉS)
  // ============================================================
  const fetchResultats = useCallback(async (page = 0) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      let url = resultType === 'rich'
        ? `${APP_CONFIG.BASE_URL}/api/v1/client/rich/resultats`
        : `${APP_CONFIG.BASE_URL}/api/v1/client/resultats`;
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('size', pagination.size);
      if (evaluationId) params.set('evaluationId', evaluationId);
      if (statut) params.set('statut', encodeURIComponent(statut));
      url += `?${params.toString()}`;

      const res = await fetch(url, { headers: authHeaders });
      if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
      const data = await res.json();
      const content = data.content || data.data || data || [];
      setResultats(Array.isArray(content) ? content : []);
      setPagination((prev) => ({
        ...prev,
        page: data.number ?? data.page ?? page,
        totalPages: data.totalPages || 0,
        totalElements: data.totalElements || content.length,
      }));
    } catch (err) {
      console.error('Erreur récupération résultats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, evaluationId, statut, pagination.size, authHeaders, resultType]);

  useEffect(() => {
    fetchResultats(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluationId, statut, resultType]);

  // ============================================================
  // 5. APPRÉCIATION D'UN RÉSULTAT
  // ============================================================
  const openAppreciation = (resultat) => {
    setAppreciationTarget(resultat);
    setAppreciationValue(resultat.appreciation ?? 5);
    setCommentaireValue(resultat.commentaire ?? '');
  };

  const saveAppreciation = async () => {
    if (!appreciationTarget) return;
    setSavingAppreciation(true);
    setError(null);
    try {
      const endpoint = resultType === 'rich'
        ? `/api/v1/client/rich/resultats/${appreciationTarget.id}/appreciation`
        : `/api/v1/client/resultats/${appreciationTarget.id}/appreciation`;
      const res = await fetch(`${APP_CONFIG.BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ appreciation: appreciationValue, commentaire: commentaireValue }),
      });
      if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
      setAppreciationTarget(null);
      fetchResultats(pagination.page);
    } catch (err) {
      console.error('Erreur enregistrement appréciation:', err);
      setError(err.message);
    } finally {
      setSavingAppreciation(false);
    }
  };

  // ============================================================
  // 6. GÉNÉRATION / TÉLÉCHARGEMENT DU CERTIFICAT
  // ============================================================
  const downloadCertificate = async (resultat) => {
    setDownloadingId(resultat.id);
    setError(null);
    try {
      const endpoint = resultType === 'rich'
        ? `/api/v1/client/rich/resultats/${resultat.id}/certificate`
        : `/api/v1/client/resultats/${resultat.id}/certificate`;
      const res = await fetch(`${APP_CONFIG.BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const nom = `${resultat.etudiantPrenom || resultat.etudiant?.prenom || ''}_${resultat.etudiantNom || resultat.etudiant?.nom || 'certificat'}`.replace(/\s+/g, '_');
      a.download = `certificat_${nom}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur génération certificat:', err);
      setError(err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  if (!isAuthenticated || !token) {
    return (
      <div className="p-6 text-center">
        <AlertCircle size={24} className="mx-auto text-red-500 mb-2" />
        <p className="text-gray-700 font-medium">Session expirée, veuillez vous reconnecter.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 bg-slate-50 min-h-screen font-sans">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tighter flex items-center gap-3">
          <Award size={28} className="text-green-600" />
          Résultats des étudiants
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Consultez les résultats, ajoutez une appréciation et générez les certificats
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center justify-between">
          <span className="flex items-center gap-2"><AlertCircle size={14} /> {error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}

      {/* Stats globales (inchangées) */}
      {rapport && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Étudiants" value={rapport.totalEtudiants ?? 0} icon={Users} color="bg-blue-500" />
          <StatCard title="Évaluations" value={rapport.totalEvaluations ?? 0} icon={FileQuestion} color="bg-purple-500" />
          <StatCard
            title="Taux de réussite"
            value={rapport.tauxReussite != null ? `${Math.round(rapport.tauxReussite)}%` : '—'}
            icon={TrendingUp}
            color="bg-green-500"
          />
          <StatCard
            title="Progression moyenne"
            value={rapport.progressionMoyenne != null ? `${Math.round(rapport.progressionMoyenne)}%` : '—'}
            icon={BarChart3}
            color="bg-amber-500"
          />
        </div>
      )}

      {/* Sélecteur de type de résultat */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col md:flex-row gap-3 items-start md:items-center">
        <div className="flex items-center gap-2 text-sm text-slate-600 font-semibold">
          <FileQuestion size={16} className="text-green-600" /> Type :
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setResultType('classic')}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
              resultType === 'classic'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Classique
          </button>
          <button
            onClick={() => setResultType('rich')}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
              resultType === 'rich'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Enrichie
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <select
            value={evaluationId}
            onChange={(e) => setEvaluationId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm appearance-none bg-white"
          >
            <option value="">Toutes les évaluations</option>
            {evaluations.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.titre}</option>
            ))}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <select
            value={statut}
            onChange={(e) => setStatut(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm appearance-none bg-white"
          >
            {STATUTS_FILTRE.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Tableau des résultats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Étudiant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Évaluation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appréciation</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center">
                    <Loader2 className="animate-spin mx-auto text-green-600" size={24} />
                    <p className="text-xs text-gray-500 mt-2">Chargement...</p>
                  </td>
                </tr>
              ) : resultats.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center">
                    <GraduationCap size={40} className="mx-auto text-gray-300" />
                    <p className="text-sm text-gray-400 mt-2">Aucun résultat pour ces filtres</p>
                  </td>
                </tr>
              ) : (
                resultats.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900 text-sm">
                        {r.etudiantPrenom || r.etudiant?.prenom} {r.etudiantNom || r.etudiant?.nom}
                      </div>
                      <div className="text-[10px] text-gray-400">{r.etudiantEmail || r.etudiant?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {r.evaluationTitre || r.titre || r.formationTitre || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-800">
                      {r.score != null ? `${r.score}%` : '—'}
                    </td>
                    <td className="px-4 py-3"><StatutBadge statut={r.statut} /></td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {r.appreciation != null ? (
                        <span className="flex items-center gap-1">
                          <Star size={12} className="text-amber-400 fill-amber-400" /> {r.appreciation}/5
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openAppreciation(r)}
                          className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                          title="Ajouter une appréciation"
                        >
                          <MessageSquare size={16} />
                        </button>
                        <button
                          onClick={() => downloadCertificate(r)}
                          disabled={r.statut !== 'REUSSI' || downloadingId === r.id}
                          className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
                          title={r.statut !== 'REUSSI' ? 'Certificat disponible uniquement pour un résultat réussi' : 'Télécharger le certificat'}
                        >
                          {downloadingId === r.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex justify-between items-center px-4 py-3 border-t border-gray-200">
            <span className="text-xs text-gray-500">
              {pagination.totalElements} résultat(s) - Page {pagination.page + 1}/{pagination.totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => fetchResultats(pagination.page - 1)}
                disabled={pagination.page === 0}
                className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 text-sm"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => fetchResultats(pagination.page + 1)}
                disabled={pagination.page + 1 >= pagination.totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 text-sm"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL : APPRÉCIATION (inchangé) */}
      {appreciationTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <MessageSquare size={20} className="text-green-600" />
                Appréciation
              </h3>
              <button onClick={() => setAppreciationTarget(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {appreciationTarget.etudiantPrenom || appreciationTarget.etudiant?.prenom}{' '}
              {appreciationTarget.etudiantNom || appreciationTarget.etudiant?.nom}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (0 à 5)</label>
                <input
                  type="number"
                  min={0}
                  max={5}
                  step={0.5}
                  value={appreciationValue}
                  onChange={(e) => setAppreciationValue(parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire</label>
                <textarea
                  rows={4}
                  value={commentaireValue}
                  onChange={(e) => setCommentaireValue(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setAppreciationTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
              >
                Annuler
              </button>
              <button
                onClick={saveAppreciation}
                disabled={savingAppreciation}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm flex items-center gap-2"
              >
                {savingAppreciation && <Loader2 size={14} className="animate-spin" />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}